import { Injectable } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { SessionExpiryService } from './session-expiry.service';

// Mismos patrones que SupabaseService usa para detectar una sesión vencida en
// las respuestas HTTP crudas (ver `isAuthErrorResponse` en supabase.service.ts).
// Se duplican aquí porque los errores que llegan a este punto ya vienen
// serializados por supabase-js (PostgrestError/FunctionsHttpError), no como
// Response, así que no podemos reutilizar el método privado directamente.
const AUTH_ERROR_PATTERNS = [
    'jwt expired',
    'jwt has expired',
    'invalid jwt',
    'not authorized',
    'unauthorized',
    'forbidden',
    'permission denied',
    'token expired',
    'session expired',
    'autentica'
];

@Injectable({
    providedIn: 'root'
})
export class HttpCoreService {
    constructor(
        private supabaseService: SupabaseService,
        private sessionExpiryService: SessionExpiryService
    ) { }

    /**
      * Ejecuta cualquier función RPC (Stored Procedure) en Supabase.
      * @param endpoint Nombre de la función en la BD (ej: 'user__upsert')
      * @param params Objeto con los parámetros que pide la función (ej: { p_nombre: 'Juan' })
      */
    async executeRequest<T>(endpoint: string, params: any = {}): Promise<any> {

        const { data, error } = await this.supabaseService.client
            .rpc(endpoint, params);

        if (error) {
            console.error(`[HttpCoreService] Error en RPC [${endpoint}]:`, error);
            this.EstatusError(error);
            throw error;
        }

        return data as T;
    }

    /**
      * Ejecuta una función RPC paginada recorriendo todas sus páginas y devuelve todos los registros.
      * Usar para exportaciones y selectores: las funciones de listado limitan p_page_size (normalmente a 100).
      * Requiere que la función responda con { result, total_pages }.
      * @param endpoint Nombre de la función en la BD (ej: 'business__list')
      * @param params Filtros de la función, sin p_page ni p_page_size
      * @param pageSize Tamaño de página por llamada (no debe superar el límite de la función)
      */
    async executeRequestAllPages<T = any>(endpoint: string, params: any = {}, pageSize = 100): Promise<T[]> {
        const records: T[] = [];
        let page = 1;
        let totalPages = 1;

        do {
            const response = await this.executeRequest(endpoint, {
                ...params,
                p_page: page,
                p_page_size: pageSize
            });

            if (!response?.result) break;

            records.push(...response.result);
            totalPages = response.total_pages || 1;
            page++;
        } while (page <= totalPages);

        return records;
    }

    /**
     * Ejecuta una Edge Function de Supabase.
     * @param functionName Nombre de la Edge Function
     * @param params Objeto con los parámetros que pide la función
     */
    async executeEdgeFunction<T>(functionName: string, params: any = {}): Promise<any> {
        try {
            // Obtener token de sesión actual para autenticación
            const { data: { session } } = await this.supabaseService.client.auth.getSession();
            const accessToken = session?.access_token || this.supabaseService.supabaseKey;

            const { data, error } = await this.supabaseService.client.functions
                .invoke(functionName, {
                    body: params,
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });

            if (error) {
                console.error(`[HttpCoreService] Error en Edge Function [${functionName}]:`, error);
                this.EstatusError(error);
                throw error;
            }

            return data as T;
        } catch (error) {
            console.error(`[HttpCoreService] Error en Edge Function [${functionName}]:`, error);
            this.EstatusError(error);
            throw error;
        }
    }

    /**
     * Ejecuta una Edge Function de Supabase enviando FormData (ideal para archivos).
     * @param functionName Nombre de la Edge Function
     * @param formData Objeto FormData que contiene los archivos y otros campos
     */
    async executeEdgeFunctionFormData<T>(functionName: string, formData: FormData): Promise<any> {
        try {
            // Obtener token de sesión actual para autenticación
            const { data: { session } } = await this.supabaseService.client.auth.getSession();
            const accessToken = session?.access_token || this.supabaseService.supabaseKey;

            const { data, error } = await this.supabaseService.client.functions
                .invoke(functionName, {
                    body: formData,
                    headers: {
                        'Authorization': `Bearer ${accessToken}`
                    }
                });

            if (error) {
                console.error(`[HttpCoreService] Error en Edge Function (FormData) [${functionName}]:`, error);
                this.EstatusError(error);
                throw error;
            }

            return data as T;
        } catch (error) {
            console.error(`[HttpCoreService] Error en Edge Function (FormData) [${functionName}]:`, error);
            this.EstatusError(error);
            throw error;
        }
    }

    /**
     * Ejecuta una Edge Function de Supabase que retorna un archivo (blob).
     * @param functionName Nombre de la Edge Function
     * @param params Objeto con los parámetros que pide la función
     * @param responseType Tipo de respuesta esperada ('blob' para archivos)
     */
    async executeEdgeFunctionFile(functionName: string, params: any = {}, responseType: 'blob' | 'json' = 'blob'): Promise<any> {
        try {
            // Obtener token de sesión actual para autenticación
            const { data: { session } } = await this.supabaseService.client.auth.getSession();
            const accessToken = session?.access_token || this.supabaseService.supabaseKey;

            const response = await fetch(`${this.supabaseService.supabaseUrl}/functions/v1/${functionName}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`,
                    'apikey': this.supabaseService.supabaseKey
                },
                body: JSON.stringify(params)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error ${response.status}: ${errorText}`);
            }

            if (responseType === 'blob') {
                const blob = await response.blob();
                const filename = this.getFilenameFromResponse(response) || 'archivo.xlsx';
                return { blob, filename };
            } else {
                return await response.json();
            }
        } catch (error) {
            console.error(`[HttpCoreService] Error en Edge Function File [${functionName}]:`, error);
            this.EstatusError(error);
            throw error;
        }
    }

    private getFilenameFromResponse(response: Response): string | null {
        const contentDisposition = response.headers.get('Content-Disposition');
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (filenameMatch && filenameMatch[1]) {
                return filenameMatch[1].replace(/['"]/g, '');
            }
        }
        return null;
    }


    EstatusError(err: any): any {
        if (!err) return;

        if (this.isAuthError(err)) {
            // Idempotente y ya maneja signOut + limpieza de storage + redirect
            // según el contexto (company/user) — ver SessionExpiryService.
            this.sessionExpiryService.handleSessionExpired(err.message);
            return;
        }

        return `${err.message} ${(err.error != null ? err.error.innerException : '')}`;
    }

    private isAuthError(err: any): boolean {
        const status = err?.status ?? err?.statusCode ?? err?.code;
        if (status === 401 || status === 403 || status === '401' || status === '403') {
            return true;
        }

        const message = err?.message?.toString().toLowerCase() ?? '';
        return AUTH_ERROR_PATTERNS.some(pattern => message.includes(pattern));
    }
}
